import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Button,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography
} from 'antd';
import { removeVietnameseTones, handlePositiveNumberInputKeyDown } from '@/utils/common';
import { useState } from 'react';
import { AGE_TYPE } from '@/constants/type';

type ProductSliderSearchProps = {
  onSearch: (values: FilterNutritionStandard) => void;
  listDishAge: NutritionAge[];
  listHealthStatus: NutritionHeathStatus[];
  listGender: NutritionGender[];
  listPhysicalActivity: NutritionPhysicalActivity[];
  listHealthStatusZCore: NutritionHealthStatusZCore60[];
};
export const NutritionStandardsManagementSearcher = ({
  onSearch,
  listDishAge,
  listHealthStatus,
  listGender,
  listPhysicalActivity,
  listHealthStatusZCore
}: ProductSliderSearchProps) => {
  const [form] = Form.useForm();

  const [isZscoreEnabled, setIsZscoreEnabled] = useState(false);
  const [isBmiChildEnabled, setIsBmiChildEnabled] = useState(false);
  const [isBmiAdultEnabled, setIsBmiAdultEnabled] = useState(false);

  const zscoreOptions = listHealthStatusZCore
    .filter(item => item.type === AGE_TYPE.ZSCORE_60)
    .map(item => ({
      value: item.id,
      label: item.description
    }));

  const bmiChildOptions = listHealthStatusZCore
    .filter(item => item.type === AGE_TYPE.BMI_60_MONTH_19_AGE)
    .map(item => ({
      value: item.id,
      label: item.description
    }));

  const bmiAdultOptions = listHealthStatusZCore
    .filter(item => item.type === AGE_TYPE.BMI_GE_19_AGE)
    .map(item => ({
      value: item.id,
      label: item.description
    }));

  const ageDishGroupOptions = listDishAge.map((item) => ({
    value: item.id,
    label: item.description,
    healthStatusBmiType: item.healthStatusBmiType
  }));

  const heathStatusGroupOptions = listHealthStatus.map((item) => ({
    value: item.healthStatusId,
    label: item.displayName
  }));

  const genderGroupOptions = listGender.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const physicalActivityGroupOptions = listPhysicalActivity.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const handleAgeChange = (values: string[]) => {
    if (!values?.length) {
      setIsZscoreEnabled(false);
      setIsBmiChildEnabled(false);
      setIsBmiAdultEnabled(false);
      form.setFieldsValue({
        zscoreNutrition: undefined,
        bmiChildNutrition: undefined,
        bmiAdultNutrition: undefined
      });
      return;
    }

    const selectedAges = listDishAge.filter(item => values.includes(item.id));
    const types = selectedAges.map(age => age.healthStatusBmiType);


    const hasZscore = types.includes(AGE_TYPE.ZSCORE_60);
    const hasBmiChild = types.includes(AGE_TYPE.BMI_60_MONTH_19_AGE);
    const hasBmiAdult = types.includes(AGE_TYPE.BMI_GE_19_AGE);

    setIsZscoreEnabled(hasZscore);
    setIsBmiChildEnabled(hasBmiChild);
    setIsBmiAdultEnabled(hasBmiAdult);

    const resetFields: { [key: string]: undefined } = {};
    if (!hasZscore) resetFields.zscoreNutrition = undefined;
    if (!hasBmiChild) resetFields.bmiChildNutrition = undefined;
    if (!hasBmiAdult) resetFields.bmiAdultNutrition = undefined;
    form.setFieldsValue(resetFields);
  };

  const handleSearch = () => {
    const values = form.getFieldsValue();

    const startAt = values.updatedTime?.[0]
      ? values.updatedTime[0].format('YYYY-MM-DD')
      : '';
    const endAt = values.updatedTime?.[1]
      ? values.updatedTime[1].format('YYYY-MM-DD')
      : '';

    const filter: FilterNutritionStandard = {
      AGE_RANGE_IDS: values.dishAge && values.dishAge.length > 0 ? values.dishAge : null,
      HEALTH_STATUS_IDS:
        values.healthStatus && values.healthStatus.length > 0
          ? values.healthStatus
          : null,
      GENDER_ID: values.gender || null,
      PHYSICAL_ACTIVITY_ID: values.activity || null,
      WEIGHT_MIN: values.weightMin || null,
      WEIGHT_MAX: values.weightMax || null,
      NUTRITION_STATUS_ZSCORE: values.zscoreNutrition || '',
      NUTRITION_STATUS_BMI_CHILD: values.bmiChildNutrition || '',
      NUTRITION_STATUS_BMI_ADULT: values.bmiAdultNutrition || '',
      UPDATED_AT_FROM: startAt,
      UPDATED_AT_TO: endAt
    };
    onSearch(filter);
  };

  const handleClear = () => {
    form.resetFields();
    setIsZscoreEnabled(false);
    setIsBmiChildEnabled(false);
    setIsBmiAdultEnabled(false);
    onSearch({});
  };

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <Typography.Title css={titleStyles} level={5}>
          BỘ LỌC
        </Typography.Title>
      ),
      children: (
        <Form form={form} css={formStyles}>
          <Row gutter={16} align='stretch'>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Độ tuổi'
                name='dishAge'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  showSearch
                  mode='multiple'
                  allowClear
                  options={ageDishGroupOptions}
                  onChange={handleAgeChange}
                  optionFilterProp='label'
                  filterOption={(input, option) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='healthStatus'
                label='Tình trạng sức khoẻ'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  options={heathStatusGroupOptions}
                  showSearch
                  optionFilterProp='label'
                  allowClear
                  filterOption={(input, option) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='gender'
                label='Giới tính'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select allowClear options={genderGroupOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} align='stretch' css={paddingSearch}>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='activity'
                label='Hoạt động thể lực'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select allowClear options={physicalActivityGroupOptions}
                />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Cân nặng (kg)'
                css={customLabel}
                colon={false}
                labelCol={{ span: 8 }}
              >
                <Input.Group compact style={{ display: 'flex' }}>
                  <Form.Item name='weightMin' noStyle>
                    <Input style={{ width: '50%' }} onKeyDown={handlePositiveNumberInputKeyDown} />
                  </Form.Item>
                  <span style={{ alignSelf: 'center', padding: '0 0.8rem' }}>-</span>
                  <Form.Item name='weightMax' noStyle>
                    <Input style={{ width: '50%' }} onKeyDown={handlePositiveNumberInputKeyDown} />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='updatedTime'
                label='Thời gian cập nhật'
                css={customLabel}
                colon={false}
                labelCol={{ span: 8 }}
              >
                <DatePicker.RangePicker
                  placeholder={['', '']}
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} align='stretch' css={paddingSearch}>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='zscoreNutrition'
                label={<span css={css`white-space: normal;`}>
                  Tình trạng dinh dưỡng theo Zscore<br />(&lt;60 tháng tuổi)
                </span>}
                css={customLabel}
                colon={false}
                labelCol={{ span: 8 }}
              >
                <Select
                  allowClear
                  options={zscoreOptions}
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input: string, option: any) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  disabled={!isZscoreEnabled}
                />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='bmiChildNutrition'
                label={<span css={css`white-space: normal;`}>
                  Tình trạng dinh dưỡng theo BMI<br />(60 tháng - 19 tuổi)
                </span>}
                css={customLabel}
                colon={false}
                labelCol={{ span: 8 }}
              >
                <Select
                  allowClear
                  options={bmiChildOptions}
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input: string, option: any) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  disabled={!isBmiChildEnabled}
                />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='bmiAdultNutrition'
                label={<span css={css`white-space: normal;`}>
                  Tình trạng dinh dưỡng theo BMI<br />(&gt;19 tuổi)
                </span>}
                css={customLabel}
                colon={false}
                labelCol={{ span: 8 }}
              >
                <Select
                  allowClear
                  options={bmiAdultOptions}
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input: string, option: any) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  disabled={!isBmiAdultEnabled}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={16}
            align='middle'
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1rem'
            }}
          >
            <Button
              type='default'
              icon={<ReloadOutlined />}
              css={resetButton}
              onClick={handleClear}
            >
              Đặt lại
            </Button>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              css={searchButton}
              onClick={handleSearch}
            >
              Tìm kiếm
            </Button>
          </Row>
        </Form>
      )
    }
  ];

  return (
    <Row css={boxStyles} justify='space-between' align='middle'>
      <Collapse
        items={items}
        defaultActiveKey={'1'}
        expandIconPosition={'end'}
        css={collapseStyles}
      />
    </Row>
  );
};

const boxStyles = css`
  padding: 0 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const collapseStyles = css`
  width: 100%;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  background-color: transparent !important;

  .ant-collapse-header {
    width: fit-content;
    border-radius: 0.8rem !important;
    border: none !important;
    padding: 0 !important;

    .ant-collapse-expand-icon {
      align-self: center;
    }
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: none !important;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
  font-weight: 700 !important;
`;

const formStyles = css`
  margin-top: 0.5rem;
  padding: 1rem;
  gap: 1rem;
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    max-width: 33.33%;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  input,
  .ant-select,
  .ant-picker {
    flex: 1;
    min-width: 6rem;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;
  }

  width: 100%;
  margin-bottom: 0;
`;

const resetButton = css`
  margin-right: 10px;
  color: #007bff;
  border-color: #007bff;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;
const paddingSearch = css`
  margin-top: 1rem;
`;
// const infoIconStyle = css`
//   color: var(--success-color) !important;
// `;
